import { useState, useEffect, useCallback } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import TableCard from '../../components/TableCard/TableCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import BatchDrawer from '../../components/BatchDrawer/BatchDrawer';
import StatCard from '../../components/StatCard/StatCard';
import { fetchBatches, updateBatchStatus, sendNotification } from '../../lib/directus';
import styles from './BatchApprovals.module.css';

function mapBatch(b) {
  return {
    id: b.id,
    batchId: '#BCH-' + String(b.id).slice(-8).toUpperCase(),
    seller: b.seller?.company_name || '—',
    sellerUserId: b.seller?.contact || null,
    commodity: b.commodity || '',
    grade: b.grade || '',
    qty: b.quantity != null ? `${b.quantity}${b.unit_of_measure ? ' ' + b.unit_of_measure : ''}` : '',
    location: b.location || '',
    status: b.status || 'Pending',
    moistureContent: b.moisture_content != null ? `${b.moisture_content}%` : null,
    foreignMatter: b.foreign_matter != null ? `${b.foreign_matter}%` : null,
    brokenDamaged: b.damaged != null ? `${b.damaged}%` : null,
    warehouse: b.location || '',
    submittedAt: b.date_created ? new Date(b.date_created).toLocaleString() : null,
    certificates: b.certificates || [],
  };
}

export default function BatchApprovals() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [commodity, setCommodity] = useState('All commodities');
  const [grade, setGrade] = useState('All Grades');
  const [status, setStatus] = useState('All status');
  const [selected, setSelected] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchBatches()
      .then(data => setBatches(data.map(mapBatch)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingCount   = batches.filter(b => b.status === 'Pending').length;
  const approvedCount  = batches.filter(b => b.status === 'Approved').length;
  const rejectedCount  = batches.filter(b => b.status === 'Rejected').length;

  const filtered = batches.filter((row) => {
    const matchSearch = row.batchId.toLowerCase().includes(search.toLowerCase()) || row.seller.toLowerCase().includes(search.toLowerCase());
    const matchCommodity = commodity === 'All commodities' || row.commodity === commodity;
    const matchGrade = grade === 'All Grades' || row.grade === grade;
    const matchStatus = status === 'All status' || row.status === status;
    return matchSearch && matchCommodity && matchGrade && matchStatus;
  });

  const toggleRow = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map((r) => r.id));

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search batches...."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <CustomSelect value={commodity} onChange={setCommodity} options={['All commodities', 'Wheat', 'Sorghum', 'Soy', 'Maize']} />
        <CustomSelect value={grade}     onChange={setGrade}     options={['All Grades', 'Grade A', 'Grade B', 'Grade C']} />
        <CustomSelect value={status}    onChange={setStatus}    options={['All status', 'Pending', 'Rejected', 'Approved']} />
      </div>
      <button className={styles.refreshBtn} onClick={() => { setSearch(''); setCommodity('All commodities'); setGrade('All Grades'); setStatus('All status'); load(); }}>
        <LuRefreshCw size={13} /> Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Batch approvals">
      <BatchDrawer
        batch={activeBatch}
        onClose={() => setActiveBatch(null)}
        onApprove={(b) => {
          updateBatchStatus(b.id, 'Approved')
            .then(() => {
              setActiveBatch(null);
              load();
              if (b.sellerUserId) {
                sendNotification(
                  b.sellerUserId,
                  'Batch approved',
                  `Your batch ${b.batchId}${b.commodity ? ` (${b.commodity}${b.grade ? ', ' + b.grade : ''})` : ''} has been approved and is now live on the marketplace.`
                ).catch(console.error);
              }
            })
            .catch(console.error);
        }}
        onReject={(b) => {
          updateBatchStatus(b.id, 'Rejected')
            .then(() => {
              setActiveBatch(null);
              load();
              if (b.sellerUserId) {
                sendNotification(
                  b.sellerUserId,
                  'Batch rejected',
                  `Your batch ${b.batchId}${b.commodity ? ` (${b.commodity}${b.grade ? ', ' + b.grade : ''})` : ''} was not approved. Please review the quality specifications and resubmit.`
                ).catch(console.error);
              }
            })
            .catch(console.error);
        }}
      />

      {/* Stat cards */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-4"><StatCard label="Pending review"  value={String(pendingCount).padStart(2, '0')} /></div>
        <div className="col-12 col-md-4"><StatCard label="Approved"        value={String(approvedCount).padStart(2, '0')} /></div>
        <div className="col-12 col-md-4"><StatCard label="Rejected"        value={String(rejectedCount).padStart(2, '0')} /></div>
      </div>

      {/* Table */}
      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleAll} />
              </th>
              <th>Batch ID</th>
              <th>Seller</th>
              <th>Commodity</th>
              <th>Qty</th>
              <th>Location</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className={styles.loadingCell}>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className={styles.loadingCell}>No batches found.</td></tr>
            )}
            {!loading && filtered.map((row) => (
              <tr key={row.id} onClick={() => setActiveBatch(row)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleRow(row.id)} />
                </td>
                <td>{row.batchId}</td>
                <td>{row.seller}</td>
                <td>{row.commodity} ({row.grade})</td>
                <td>{row.qty}</td>
                <td>{row.location}</td>
                <td><StatusBadge status={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
